import { QueryKeys } from "@Constants";
import { IActivityMetaDetails } from "@Models/IActivityDetails";
import ActivityService from "@Services/ActivityService";
import { useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import { useState } from "react";

/**
 * Custom hook for handling updates to activity meta uploads.
 * @returns A handler for value changes.
 */
const useUpdateActivityMetadata = () => {
    const [isUpdatingActivityMetadata, setIsUpdatingActivityMetadata] = useState(false);
    const queryClient = useQueryClient();
    const activityService = new ActivityService();

    /**
     * Handles changes to the activity meta details form.
     * @param _ - The changed values (unused).
     * @param model - The complete model with the changed values.
     */
    const handleUpdateActivityMetadata = AwesomeDebouncePromise(
        async (_: Partial<IActivityMetaDetails>, model: IActivityMetaDetails & { creationDate?: string }) => {
            setIsUpdatingActivityMetadata(true);
            await activityService.update(model).then(async (res) => {
                setIsUpdatingActivityMetadata(false);

                if (res.value?.id) {
                    notification.success({
                        message: `${model.title ?? "This activity"} has been updated successfully.`,
                    });

                    await queryClient.invalidateQueries([QueryKeys.ActivityCurrent]);
                    await queryClient.invalidateQueries([QueryKeys.ActivityListUser]);
                    await queryClient.invalidateQueries([QueryKeys.ActivityListPublic]);
                }
            });
        },
        1500
    );

    return { isUpdatingActivityMetadata, onUpdateActivityMetadata: handleUpdateActivityMetadata };
};

export default useUpdateActivityMetadata;
